import { ModalWrapper, useClubSettings } from "apps/portal/src/providers";
import { useQueryZapierGetIntegrationConfig } from "libs/api/src/lib/hooks/zapier";
import { useTranslation } from "react-i18next";

import useGymflowModels from "../../store";
import { Button, CopyInput, DangerIcon, Spinner } from "../atoms";
import { IOSRocketIcon } from "../atoms/icons/IOSRocketIcon";
import { XIcon } from "../atoms/icons/XIcon";

type ZapierIntegrationModalProps = {
  onCancel: () => void;
};

export const ZapierIntegrationModal = ({
  onCancel,
}: ZapierIntegrationModalProps) => {
  const { api } = useGymflowModels();
  const { clubId } = useClubSettings();
  const { t } = useTranslation();

  const { data: dataZapierConfig, isLoading: isLoadingZapierConfig } =
    useQueryZapierGetIntegrationConfig({
      api,
      clubId,
      enabled: true,
      refetchOnWindowFocus: true,
    });

  return (
    <ModalWrapper
      className="flex max-w-md flex-col gap-6"
      onCancel={() => onCancel()}
    >
      <div className="flex flex-row justify-between">
        <div className="bg-secondary-50 flex size-11 items-center justify-center rounded-full">
          <div className="bg-secondary-100 flex size-8 items-center justify-center self-center rounded-full">
            <DangerIcon
              className="size-5"
              pathClassName="stroke-secondary-600"
            />
          </div>
        </div>
        <div className="cursor-pointer" onClick={() => onCancel()}>
          <XIcon className="size-6" pathClassName="stroke-gray-600" />
        </div>
      </div>
      <div className="relative flex flex-col gap-5">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-gray-950">
            {t("components.zapierIntegrationModal.title")}
          </div>
          <div className="text-sm font-medium text-gray-500">
            {t("components.zapierIntegrationModal.description")}
          </div>
        </div>
        <div className="shadow-xs flex flex-col gap-3 rounded-xl border border-gray-200 p-3">
          <div className="flex flex-row gap-0.5 text-base font-semibold text-gray-950">
            {t("components.zapierIntegrationModal.steps.title")}
            <IOSRocketIcon className="size-6" />
          </div>
          <div className="flex flex-col gap-1">
            <div className="text-sm font-medium text-gray-500">
              {t("components.zapierIntegrationModal.steps.firstStep")}
            </div>
            <div className="text-sm font-medium text-gray-500">
              {t("components.zapierIntegrationModal.steps.secondStep")}
            </div>
            <div className="text-sm font-medium text-gray-500">
              {t("components.zapierIntegrationModal.steps.thirdStep")}
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-3">
          <CopyInput
            label={t("components.zapierIntegrationModal.clubId")}
            className="flex-1"
            copyInfo={dataZapierConfig?.clubId.toString() || ""}
          />
          <CopyInput
            label={t("components.zapierIntegrationModal.apiKey")}
            className="flex-1"
            copyInfo={dataZapierConfig?.zapierApiKey || ""}
          />
        </div>
        {isLoadingZapierConfig && <Spinner className="absolute inset-0" />}
      </div>
      <div className="flex flex-col items-center justify-center gap-4">
        <Button
          intent="secondary"
          className="!px-14"
          onClick={() => onCancel()}
        >
          {t("portal.common.gotIt")}
        </Button>
      </div>
    </ModalWrapper>
  );
};
